import { isUUID, uuidv4 } from "../../../utils/uuid";

import type { UUID } from "../../../utils/uuid";
import type { UnlayerDesign } from "../types/unlayer-design";

interface UnlayerPollValues {
  pollId?: string;
  question?: string;
  pollOptions?: string;
  showPollResults?: unknown;
  [key: string]: unknown;
}

interface UnlayerPollBlock {
  type?: string;
  values?: UnlayerPollValues;
}

interface UnlayerPollOptionValue {
  id: string;
  text: string;
}

interface PreparedUnlayerPoll {
  pollId: UUID;
  question: string;
  showPollResults: boolean;
  options: { id: UUID; text: string; ordering: number }[];
}

interface PollOptionPreparation {
  options: { id: UUID; text: string }[];
  usedOptionIds: readonly UUID[];
  changed: boolean;
}

interface PollPreparation {
  polls: PreparedUnlayerPoll[];
  usedPollIds: readonly UUID[];
  usedOptionIds: readonly UUID[];
  optionIdsChanged: boolean;
}

function cloneDesign(design: UnlayerDesign): UnlayerDesign {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion, unicorn/prefer-structured-clone -- the JSON round-trip also normalises the design to plain JSON; structuredClone would keep non-serialisable values
  return JSON.parse(JSON.stringify(design)) as UnlayerDesign;
}

function isPollOption(value: unknown): value is UnlayerPollOptionValue {
  return (
    Boolean(value) &&
    typeof value === "object" &&
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    typeof (value as { id?: unknown }).id === "string" &&
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    typeof (value as { text?: unknown }).text === "string"
  );
}

function parsePollOptions(value: string | undefined): { id: UUID; text: string }[] {
  if (!value) return [];

  try {
    const options = JSON.parse(value) as unknown;
    if (!Array.isArray(options) || !options.every(isPollOption)) return [];

    return options
      .map((option) => ({ id: option.id, text: option.text.trim() }))
      .filter(
        (option): option is { id: UUID; text: string } =>
          isUUID(option.id) && option.text.length > 0,
      );
  } catch {
    return [];
  }
}

function preparePollOptions(
  options: { id: UUID; text: string }[],
  usedOptionIds: readonly UUID[],
  replaceAll: boolean,
): PollOptionPreparation {
  return options.reduce<PollOptionPreparation>(
    (preparation, option) => {
      const shouldReplace = replaceAll || preparation.usedOptionIds.includes(option.id);
      const normalizedOption = shouldReplace ? { ...option, id: uuidv4() } : option;

      return {
        options: [...preparation.options, normalizedOption],
        usedOptionIds: [...preparation.usedOptionIds, normalizedOption.id],
        changed: preparation.changed || shouldReplace,
      };
    },
    { options: [], usedOptionIds, changed: false },
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

export function getUnlayerPollBlocks(design: UnlayerDesign): UnlayerPollBlock[] {
  const blocks: UnlayerPollBlock[] = [];

  function collect(value: unknown): void {
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }

    if (!isRecord(value)) return;

    if (typeof (value as UnlayerPollBlock).values?.pollOptions === "string") {
      blocks.push(value);
      return;
    }

    Object.values(value).forEach(collect);
  }

  collect(design);
  return blocks;
}

export function prepareUnlayerPollDesign(
  sourceDesign: UnlayerDesign,
  existingPollIds: readonly UUID[] = [],
): {
  design: UnlayerDesign;
  polls: PreparedUnlayerPoll[];
  optionIdsChanged: boolean;
} {
  const design = cloneDesign(sourceDesign);
  const preparation = getUnlayerPollBlocks(design).reduce<PollPreparation>(
    (current, block) => {
      const { values } = block;
      if (!values) return current;

      const question = values.question?.trim();
      const options = parsePollOptions(values.pollOptions);
      if (!question || options.length === 0) return current;

      const existingPollId = existingPollIds[current.polls.length];
      const preferredPollId: UUID | undefined = isUUID(values.pollId)
        ? values.pollId
        : isUUID(existingPollId)
          ? existingPollId
          : undefined;
      const pollIdIsDuplicate = preferredPollId
        ? current.usedPollIds.includes(preferredPollId)
        : false;
      const pollId = preferredPollId && !pollIdIsDuplicate ? preferredPollId : uuidv4();
      const preparedOptions = preparePollOptions(options, current.usedOptionIds, pollIdIsDuplicate);

      block.values = {
        ...values,
        pollId,
        ...(preparedOptions.changed
          ? {
              pollOptions: JSON.stringify(
                preparedOptions.options.map(({ id, text }) => ({ id, text })),
              ),
            }
          : {}),
      };

      return {
        polls: [
          ...current.polls,
          {
            pollId,
            question,
            showPollResults: values.showPollResults === true,
            options: preparedOptions.options.map((option, index) => ({
              // oxlint-disable-next-line oxc/no-accumulating-spread -- `option` is a map() element, not the reduce accumulator
              ...option,
              ordering: index + 1,
            })),
          },
        ],
        usedPollIds: [...current.usedPollIds, pollId],
        usedOptionIds: preparedOptions.usedOptionIds,
        optionIdsChanged: current.optionIdsChanged || preparedOptions.changed,
      };
    },
    { polls: [], usedPollIds: [], usedOptionIds: [], optionIdsChanged: false },
  );

  return {
    design,
    polls: preparation.polls,
    optionIdsChanged: preparation.optionIdsChanged,
  };
}
